import React, { forwardRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import withTheme from '../styles/withTheme'
import useControlled from '../../utils/useControlled'
import UncontrolledQuantitySelector from './UncontrolledQuantitySelector'

import './QuantitySelector.scss'
import ControlledQuantitySelector from './ControlledQuantitySelector'

const QuantitySelector = forwardRef(function QuantitySelector(props, ref) {

	const {
		className,
		inputRef,
		value: valueProp,
		defaultValue,
		min,
		max,
		step = 1,
		debouncing: debouncingTime = 500,
		disabled,
		onChange,
		onValueChange, // DEPRECATED: use onChange instead
		...other
	} = props

	const [value, setValue] = useControlled({
		controlled: valueProp,
		default: defaultValue ?? min ?? 0
	})
	const [previousValue, setPreviousValue] = useState(value)

	const onChangeDebounced = _.debounce((v) => {
		onChange?.(v)
		onValueChange?.(v)
	}, debouncingTime)

	const isControlled = valueProp !== undefined && valueProp !== null

	useEffect(() => {
		if (value === previousValue) {
			return
		}
		setPreviousValue(value)
		!isControlled && onChangeDebounced(Number(value))
		return onChangeDebounced.cancel
	}, [value])

	const handleUncontrolledChange = (newValue) => {
		setValue(newValue)
	}

	const handleControlledChange = (newValue) => {
		newValue = (newValue === '' || newValue === '.') ? '' : Number(newValue)
		onChange(newValue)
		onValueChange?.(newValue)
	}

	return (
		isControlled ? (
			<ControlledQuantitySelector
				className={className}
				ref={ref}
				min={min}
				max={max}
				inputRef={inputRef}
				step={step}
				value={value}
				debouncingTime={debouncingTime}
				onChange={handleControlledChange}
				disabled={disabled}
				{...other}
			/>
		) : (
			<UncontrolledQuantitySelector
				className={className}
				ref={ref}
				min={min}
				max={max}
				inputRef={inputRef}
				step={step}
				value={value}
				debouncingTime={debouncingTime}
				onChange={handleUncontrolledChange}
				disabled={disabled}
				{...other}
			/>
		)
	)
})

export default withTheme('Vb-qty-selector')(QuantitySelector)


QuantitySelector.propTypes = {
	/**
	 * Default value of the uncontrolled component.
	 */
	defaultValue: PropTypes.number,

	/**
	 * Debouncing time in ms.
	 * @default 500
	 */
	debouncing: PropTypes.number,

	/**
	 * If `true`, the input and buttons are disabled.
	 * @default false
	 */
	disabled: PropTypes.bool,

	/**
	 * Max value allowed.
	 */
	max: PropTypes.number,

	/**
	 * Min value allowed.
	 */
	min: PropTypes.number,

	/**
	 * Callback fired when value changed and it's controlled.
	 *
	 * @param {number} value The input value.
	 * @param {'added' | 'substracted' | 'typed'} reason The reason why the value is been updated.
	 */
	onChange: PropTypes.func,

	/**
	 * Step value.
	 */
	step: PropTypes.number,

	/**
	 * Controlled value.
	 */
	value: PropTypes.number
}